import { useParams, Link, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { ExternalLink, Share2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { DarkHero, Loader, Empty, NewsCard, NewsRow, SportBadge, AthleteCard, VideoCard } from "@/components/Blocks";
import { PhotoGallery } from "@/components/PhotoGallery";

const GALLERY_Q = { football: "Cameroon national football team", basketball: "Cameroon basketball", athletics: "Cameroon athletics", combat: "Cameroon boxing", handball: "Cameroon handball", volleyball: "Cameroon volleyball", women: "Cameroon women's national football team", more: "Cameroon sport" };
import { TeamCard, CompetitionCard } from "@/pages/SportHub";
import { timeAgo, fmtDate, sportLabel, PLACEHOLDER, FLAG, onImgError } from "@/lib/utils";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Trophy, TrendingUp } from "lucide-react";

export function ArticlePage() {
  const { slug } = useParams();
  const { t } = useT();
  const { data: a, isLoading, error } = useApi("article", `/news/${slug}`);
  if (isLoading) return <Loader />;
  if (error || !a) return <div className="container-x py-20"><Empty text="Article not found." /></div>;
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); };
  return (
    <article data-testid="article-page">
      <div className="container-x max-w-4xl py-10">
        <SportBadge sport={a.sport} className="text-xs" />
        <h1 className="mt-3 font-display text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="article-title">{a.title}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
          <span>By {a.author || a.source}</span><span>·</span><span>{fmtDate(a.published_at)}</span><span>·</span><span>{timeAgo(a.published_at)}</span>
          <button onClick={share} className="ml-auto flex items-center gap-1 text-slate-700 hover:text-[#047857]" data-testid="article-share-btn"><Share2 size={16} /> {t("Share")}</button>
        </div>
        <div className="mt-6 overflow-hidden rounded-2xl"><img src={a.image || PLACEHOLDER} onError={onImgError} alt="" className="aspect-[16/8] w-full object-cover" /></div>
        <p className="mt-8 text-xl leading-relaxed text-slate-800" data-testid="article-summary">{a.summary}</p>
        {a.content && <div className="prose mt-6 max-w-none whitespace-pre-line leading-relaxed text-slate-700" data-testid="article-content">{a.content}</div>}
        {a.kind === "news" && a.source_url && (
          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="article-source-box">
            <p className="text-sm text-slate-600">This headline was aggregated from <b>{a.source}</b> via Google News. Read the full report at the original publisher.</p>
            <a href={a.source_url} target="_blank" rel="noreferrer" className="btn-green mt-3" data-testid="article-source-link">{t("Read full story")} <ExternalLink size={14} /></a>
          </div>
        )}
        <p className="mt-6 text-xs text-slate-400">Fetched {fmtDate(a.fetched_at)} · Last updated {fmtDate(a.updated_at)}</p>
        {a.athlete && <div className="mt-10"><h3 className="mb-4 font-display text-2xl font-extrabold">Featured Athlete</h3><div className="w-48"><AthleteCard a={a.athlete} /></div></div>}
        <PhotoGallery q={a.athlete?.name || GALLERY_Q[a.sport] || "Cameroon sport"} />
      </div>
      <div className="border-t border-slate-100 bg-slate-50 py-12"><div className="container-x"><h3 className="mb-6 font-display text-3xl font-extrabold">{t("Related Stories")}</h3><div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{a.related.map((n) => <NewsCard key={n.slug} item={n} />)}</div></div></div>
    </article>
  );
}

const SPORTS = ["all", "football", "basketball", "athletics", "combat", "handball", "women", "volleyball", "more"];

export function NewsList() {
  const { t } = useT();
  const [sport, setSport] = useState("all");
  const { data, isLoading } = useApi("newslist", `/news?limit=48${sport !== "all" ? `&sport=${sport}` : ""}`);
  return (
    <div data-testid="news-list-page">
      <DarkHero title="News" subtitle="Latest Cameroonian sports headlines, aggregated live from trusted publishers." small />
      <div className="container-x py-8">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">{SPORTS.map((s) => <button key={s} onClick={() => setSport(s)} data-testid={`news-filter-${s}`} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold ${sport === s ? "bg-[#047857] text-white" : "bg-slate-100 text-slate-700"}`}>{s === "all" ? t("All") : t(sportLabel(s))}</button>)}</div>
        {isLoading ? <Loader /> : <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data?.items?.length ? data.items.map((n) => <NewsCard key={n.slug} item={n} />) : <Empty />}</div>}
      </div>
    </div>
  );
}

export function SearchPage() {
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const { data, isLoading } = useApi("search", q ? `/search?q=${encodeURIComponent(q)}` : null);
  const total = data ? data.news.length + data.athletes.length + data.teams.length + data.competitions.length : 0;
  return (
    <div data-testid="search-page">
      <DarkHero title="Search" subtitle={q ? `Results for "${q}"` : "Search athletes, news, teams and competitions."} small />
      <div className="container-x py-8">
        {isLoading ? <Loader /> : !data ? null : total === 0 ? <Empty text={`No results for "${q}".`} /> : (
          <div className="space-y-12">
            <p className="text-sm text-slate-500" data-testid="search-count">{total} results</p>
            {data.athletes.length > 0 && <section><h2 className="mb-4 font-display text-2xl font-extrabold">Athletes</h2><div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">{data.athletes.map((a) => <AthleteCard key={a.slug} a={a} />)}</div></section>}
            {data.news.length > 0 && <section><h2 className="mb-4 font-display text-2xl font-extrabold">News</h2><div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data.news.map((n) => <NewsCard key={n.slug} item={n} />)}</div></section>}
            {data.teams.length > 0 && <section><h2 className="mb-4 font-display text-2xl font-extrabold">Teams</h2><div className="grid gap-4 sm:grid-cols-3">{data.teams.map((t) => <TeamCard key={t.slug} t={t} />)}</div></section>}
            {data.competitions.length > 0 && <section><h2 className="mb-4 font-display text-2xl font-extrabold">Competitions</h2><div className="grid gap-4 sm:grid-cols-3">{data.competitions.map((c) => <CompetitionCard key={c.slug} c={c} />)}</div></section>}
          </div>
        )}
      </div>
    </div>
  );
}

export function VideosPage() {
  const { data, isLoading } = useApi("videos", "/videos?limit=40");
  return (
    <div data-testid="videos-page">
      <DarkHero title="Videos" subtitle="Highlights, interviews and features from official channels (CAF TV, FECAFOOT, PFL)." small />
      <div className="container-x py-8">{isLoading ? <Loader /> : <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{data?.length ? data.map((v) => <VideoCard key={v.video_id} v={v} />) : <Empty />}</div>}</div>
    </div>
  );
}

export function CompetitionsList() {
  const { data, isLoading } = useApi("competitions", "/competitions");
  return (
    <div data-testid="competitions-page">
      <DarkHero title="Competitions" subtitle="Leagues, cups and championships where Cameroon competes." small />
      <div className="container-x py-8">{isLoading ? <Loader /> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{data?.map((c) => <CompetitionCard key={c.slug} c={c} />)}</div>}</div>
    </div>
  );
}

export function AbroadPage() {
  const { t } = useT();
  const [sport, setSport] = useState("all");
  const { data, isLoading } = useApi("abroad", `/athletes?abroad=true&limit=200${sport !== "all" ? `&sport=${sport}` : ""}`);
  const { data: news } = useApi("abroadnews", "/news?limit=6&sport=football");
  const { data: tracker } = useApi("tracker", "/abroad/tracker");
  const groups = data ? Object.entries(data.reduce((acc, a) => { (acc[a.club_country] ||= []).push(a); return acc; }, {})).sort((x, y) => y[1].length - x[1].length) : [];
  return (
    <div data-testid="abroad-page">
      <DarkHero title="Cameroonians Abroad" subtitle="Tracking every Cameroonian athlete performing on the international stage — from Old Trafford to the NBA and the PFL cage." image="https://images.unsplash.com/photo-1522778119026-d647f0596c20?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600" />
      {tracker && <StarOfWeek tracker={tracker} />}
      <div className="container-x py-8">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">{["all", "football", "basketball", "athletics", "combat", "handball", "more"].map((s) => <button key={s} onClick={() => setSport(s)} data-testid={`abroad-filter-${s}`} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold ${sport === s ? "bg-[#047857] text-white" : "bg-slate-100 text-slate-700"}`}>{s === "all" ? t("All") : t(sportLabel(s))}</button>)}</div>
        {isLoading ? <Loader /> : groups.map(([country, list]) => (
          <section key={country} className="mt-10" data-testid={`abroad-group-${country.toLowerCase().replace(/\s+/g, "-")}`}>
            <h2 className="mb-4 flex items-center gap-3 font-display text-2xl font-extrabold">{country} <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600">{list.length}</span></h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">{list.map((a) => <AthleteCard key={a.slug} a={a} />)}</div>
          </section>
        ))}
        {news?.items?.length > 0 && <section className="mt-12"><h2 className="mb-4 font-display text-2xl font-extrabold">{t("Diaspora Headlines")}</h2><div className="grid gap-x-8 md:grid-cols-2">{news.items.map((n) => <NewsRow key={n.slug} item={n} />)}</div></section>}
      </div>
    </div>
  );
}

const OUT = { W: "bg-emerald-600", D: "bg-slate-500", L: "bg-red-600" };

function StarOfWeek({ tracker }) {
  const { t } = useT();
  const { star, buzz, tracker: rows, method } = tracker;
  return (
    <section className="bg-slate-50 py-12" data-testid="star-of-week-section">
      <div className="container-x grid gap-8 lg:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="section-title mb-8">{t("Star of the Week")}</h2>
          {star ? (
            <Link to={`/athletes/${star.athlete.slug}`} data-testid="star-of-week-card" className="card-hover relative grain flex overflow-hidden rounded-2xl bg-[#0B1120] text-white shadow-xl">
              <div className="w-2/5 flex-none">{star.athlete.image ? <img src={star.athlete.image} alt="" className="h-full w-full object-cover object-top" /> : <div className="h-full bg-slate-800" />}</div>
              <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-[#F5B301]"><Trophy size={14} /> {t("Star of the Week")}</p>
                  <h3 className="mt-3 flex items-center gap-2 font-display text-4xl font-black leading-none" data-testid="star-of-week-name">{star.athlete.name} <img src={FLAG} alt="" className="h-4 rounded-sm" /></h3>
                  <p className="mt-1 text-sm text-slate-300">{star.athlete.position} · {star.athlete.club}</p>
                  {star.headline && <p className="mt-4 text-sm leading-snug text-slate-200 line-clamp-3">“{star.headline.title}”<span className="block text-xs text-slate-400">{star.headline.source} · {timeAgo(star.headline.published_at)}</span></p>}
                </div>
                <div className="mt-5 flex gap-6 text-center">
                  <div><p className="font-display text-3xl font-black text-[#F5B301]">{star.mentions}</p><p className="text-[11px] uppercase tracking-wider text-slate-400">{t("mentions")}</p></div>
                  <div><p className="font-display text-3xl font-black text-emerald-400">{star.wins}</p><p className="text-[11px] uppercase tracking-wider text-slate-400">{t("wins")}</p></div>
                </div>
              </div>
            </Link>
          ) : <Empty text="Not enough headline data this week." />}
          <h3 className="mt-8 mb-3 flex items-center gap-2 font-display text-xl font-extrabold"><TrendingUp size={18} className="text-[#047857]" /> {t("Buzz Ranking")}</h3>
          <ol className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white" data-testid="buzz-ranking">
            {buzz.map((b, i) => (
              <li key={b.athlete.slug}><Link to={`/athletes/${b.athlete.slug}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50" data-testid={`buzz-row-${b.athlete.slug}`}>
                <span className="w-5 font-display text-lg font-black text-slate-400">{i + 1}</span>
                {b.athlete.image ? <img src={b.athlete.image} alt="" className="h-9 w-9 rounded-full object-cover object-top" /> : <span className="h-9 w-9 rounded-full bg-slate-200" />}
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold">{b.athlete.name}</span><span className="block truncate text-xs text-slate-500">{t(sportLabel(b.athlete.sport))} · {b.athlete.club}</span></span>
                <span className="text-xs font-semibold text-slate-600">{b.mentions} {t("mentions")}{b.wins ? ` · ${b.wins} W` : ""}</span>
              </Link></li>
            ))}
          </ol>
        </div>
        <div>
          <h2 className="section-title mb-8">{t("Weekly Performance Tracker")}</h2>
          <div className="space-y-2" data-testid="performance-tracker">
            {rows.length ? rows.map((r, i) => (
              <Link key={i} to={r.athlete.slug ? `/athletes/${r.athlete.slug}` : r.athlete.team_slug ? `/teams/${r.athlete.team_slug}` : "#"} data-testid={`tracker-row-${i}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-[#047857]">
                {r.athlete.image ? <img src={r.athlete.image} alt="" className="h-12 w-12 flex-none rounded-lg object-cover object-top" /> : <span className="h-12 w-12 flex-none rounded-lg bg-slate-200" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{r.athlete.name} <span className="font-normal text-slate-500">· {r.club}</span></p>
                  <p className="truncate text-xs text-slate-500">{r.competition} · vs {r.opponent} · {fmtDate(r.kickoff, false)}</p>
                </div>
                <span className="font-display text-xl font-black">{r.score}</span>
                <span className={`grid h-8 w-8 flex-none place-items-center rounded-md text-sm font-black text-white ${OUT[r.outcome]}`}>{r.outcome}</span>
              </Link>
            )) : <Empty text="No club results in the last 7 days for tracked stars." />}
          </div>
          <p className="mt-3 text-[11px] text-slate-400">{method}</p>
        </div>
      </div>
    </section>
  );
}

export function NotFound() {
  return <div className="container-x py-24 text-center"><h1 className="font-display text-6xl font-black">404</h1><p className="mt-2 text-slate-500">Page not found.</p><Link to="/" className="btn-green mt-6">Back home</Link></div>;
}
